import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import { LoaderFunctionArgs, redirect } from "react-router-dom";
import { apiClient, ParcelTableLoaderResult } from "~/services/apiClient";
import { LoaderData, FormattedParcelData } from "~/types";
import { getCachedAuthAccessInfo, loadCookiesData } from "~/utils";
import {
  createClientFilteredShouldRevalidate,
  setLoaderQueryData,
  STALE_TIME_TIER_A,
} from "~/utils/loaderQueryCache";
import * as APITypes from "~/API";

export type ParcelTablePageLoaderType = {
  parcelList: LoaderData<FormattedParcelData[]>;
  isAuthenticated: boolean;
};

export function parcelTableQueryKey(
  ownerID: string,
  ownerType: APITypes.OwnerType,
) {
  return ["parcel-table", ownerID, ownerType] as const;
}

export const parcelTableShouldRevalidate = createClientFilteredShouldRevalidate(
  (owner) => parcelTableQueryKey(owner.ownerID, owner.ownerType),
  STALE_TIME_TIER_A,
);

export async function fetchParcelTableData(): Promise<
  ParcelTablePageLoaderType | Response
> {
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");

  const authAccessInfo = await loadCookiesData(user.uid);
  if (!authAccessInfo.success) return redirect("/explore");

  const { ownerID, ownerType } = authAccessInfo.data;

  const result = await apiClient.loader<ParcelTableLoaderResult>(
    "parcel-table",
    { ownerID, ownerType },
  );

  const parcelListData: FormattedParcelData[] = (
    result.data?.parcelList ?? []
  )
    .filter(({ parcelDetails }) => parcelDetails._deleted !== true)
    .map(({ parcelDetails, propertyDetails }) => ({
      parcelDetails,
      propertyDetails,
    }));

  return {
    parcelList: {
      success: result.success,
      data: parcelListData,
    },
    isAuthenticated: true,
  };
}

export async function ParcelTablePageLoader({
  request,
}: LoaderFunctionArgs): Promise<ParcelTablePageLoaderType | Response> {
  void request;
  const data = await fetchParcelTableData();
  if (data instanceof Response) {
    return data;
  }

  const auth = getCachedAuthAccessInfo();
  if (auth) {
    setLoaderQueryData(
      parcelTableQueryKey(auth.ownerID, auth.ownerType),
      data,
      STALE_TIME_TIER_A,
    );
  }

  return data;
}
